import React, { useEffect } from 'react';
import { Outlet, Link, useLocation, resolvePath } from "react-router-dom";
import { MsalProvider, AuthenticatedTemplate, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import './Nav.scss';
import { Icon } from '../icons/icons';
import { isMobile } from 'react-device-detect';
import useWindowDimensions from '../../hooks/windowDimensions';
import { ROUTES } from '../../route-constants';

const Nav: React.FC = () => {
  const location = useLocation();
  const [isMobileClient, setIsMobileClient] = React.useState<boolean>(false);
  const [isApplicationMenuVisible, setIsApplicationVisible] = React.useState<boolean>(false);
  const [isHomePage, setIsHomePage] = React.useState<boolean>(true);
  const [] = React.useState(location.pathname);
  const { height, width } = useWindowDimensions();
  const [isBookmarksHovered, setIsBookmarksHovered] = React.useState(false);
  const [isHamburgerHovered, setIsHamburgerHovered] = React.useState(false);
  const [isMyCompetenciesHovered, setIsMyCompetenciesHovered] = React.useState(false);
  /**
 * useMsal is hook that returns the PublicClientApplication instance,
 * that tells you what msal is currently doing. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
 */
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  //#region Class name changes in states

  /* shared nav button container — same for all header buttons */
  const navButtonClasses = ['nav-button-container', 'dis-flex', 'dis-flex-rows', 'dis-flex-aln-center', 'gap--25'];
  if (isMobileClient) navButtonClasses.push('btn-icon');
  if (!isMobileClient && isHomePage) navButtonClasses.push('btn-outlined-white');
  if (!isMobileClient && !isHomePage) navButtonClasses.push('btn-outlined-primary');

  /* desktop icon classes — pass in hover state */
  const getNavIconClasses = (isHovered: boolean) => {
    const classes = ['nav-icon', 'nav-icon-desktop'];
    if (isHomePage && !isHovered) classes.push('icon-white');
    if (isHomePage && isHovered) classes.push('icon-primary');
    if (!isHomePage && !isHovered) classes.push('icon-primary');
    if (!isHomePage && isHovered) classes.push('icon-white');
    return classes.join(' ');
  };

  /* desktop label classes — pass in hover state */
  const getNavLabelClasses = (isHovered: boolean) => {
    const classes = ['nav-button-label'];
    if (isHomePage && !isHovered) classes.push('clr-white');
    if (isHomePage && isHovered) classes.push('clr-primary');
    if (!isHomePage && !isHovered) classes.push('clr-primary');
    if (!isHomePage && isHovered) classes.push('clr-white');
    return classes.join(' ');
  };

  /* mobile icon classes — no hover on mobile */
  const mobileNavIconClasses = ['nav-icon'];
  if (isHomePage) mobileNavIconClasses.push('icon-white');
  if (!isHomePage) mobileNavIconClasses.push('icon-primary');

  /* JMT Icon classes */
  const logoClasses = ['logo'];
  if (isHomePage) logoClasses.push('icon-white');
  if (!isHomePage) logoClasses.push('icon-primary');

  /* nav background classes */
  const navBGClasses = ['p--75'];
  if (isHomePage) navBGClasses.push('bg-primary');
  if (!isHomePage) navBGClasses.push('bg-white');

  /* H1 classes */
  const h1Classes = ['m-0', 'dis-flex', 'dis-flex-rows', 'dis-flex-aln-center', 'gap--50'];
  if (isHomePage && !isMobileClient) h1Classes.push('dis-none');
  if (isHomePage && isMobileClient) h1Classes.push('clr-white');
  if (!isHomePage) h1Classes.push('clr-primary');

  /* Hamburger classes */
  const hamburgerClasses = ['hamburger'];
  //home page
  if (isHomePage && !isHamburgerHovered) hamburgerClasses.push('icon-white');
  if (isHomePage && isHamburgerHovered) hamburgerClasses.push('icon-primary bg-white');
  //non-home page
  if (!isHomePage && !isHamburgerHovered) hamburgerClasses.push('icon-primary');
  if (!isHomePage && isHamburgerHovered) hamburgerClasses.push('icon-white bg-primary');

  /* application menu classes */
  const applicationMenuContainerClasses = ['application-menu-container'];
  if (isApplicationMenuVisible) applicationMenuContainerClasses.push('dis-block');
  if (!isApplicationMenuVisible) applicationMenuContainerClasses.push('dis-none');

  /* button section classes */
  const buttonSectionClasses = ['dis-flex', 'dis-flex-rows', 'dis-flex-aln-center', 'dis-flex-grow-1', 'dis-flex-jst-flex-end', 'gap-1'];
  if (isHomePage) buttonSectionClasses.push('button-section-container-end');
  //#endregion class names

  //#region Use Effects

  /* runs every time the URL changes */
  useEffect(() => {
    const isHome = location.pathname === '/';
    setIsHomePage(isHome);
    if (isApplicationMenuVisible) {
      setIsApplicationVisible(!isApplicationMenuVisible);
    }
  }, [location.pathname]);


  /* is it the Home page? */
  useEffect(() => {
  }, [isHomePage]);

  /* are we on a mobile device? */
  useEffect(() => {
    const finalValue = isMobile || width < 800;
    setIsMobileClient(finalValue);
  }, [width]);


  //#endregion Use Effects

  //#region Events

  const applicationMenuClick = () => {
    setIsApplicationVisible(!isApplicationMenuVisible);
  };

  var roles = instance.getActiveAccount()?.idTokenClaims?.roles;
  if (!roles) roles = [];

  //#endregion Events
  return (
    <nav className={navBGClasses.join(' ')}>
      <section className='dis-flex dis-flex-rows dis-flex-aln-center'>
      <Link to={ROUTES.home} className='bdr-none'>
        <h1 className={h1Classes.join(' ')}>
            Career Framework
            <span className='ampersand'>@</span>
            <Icon name='icon-JMTLogo' className={logoClasses.join(' ')} />
        </h1>
        </Link>
        <section className={buttonSectionClasses.join(' ')}>
          <div className='dis-flex dis-flex-rows dis-flex-aln-center gap--25'>
            <Link to={ROUTES.bookmarks.root} onMouseOver={() => setIsBookmarksHovered(true)} onMouseOut={() => setIsBookmarksHovered(false)} className={navButtonClasses.join(' ')} title="Show bookmarks" tabIndex={(isApplicationMenuVisible ? -1 : 0)}>
              {isMobileClient && (
                <>
                  <span className='sr-only'>My Bookmarks</span>
                  <Icon name='icon-btn-bookmarks-mobile' className={mobileNavIconClasses.join(' ')} />
                </>
              )}
              {!isMobileClient && (
                <>
                  <Icon name='icon-bookmark' className={getNavIconClasses(isBookmarksHovered)} />
                  <span className={getNavLabelClasses(isBookmarksHovered)}>My Bookmarks</span>
                </>
              )}
            </Link>
            <Link to={ROUTES.competencies.root} onMouseOver={() => setIsMyCompetenciesHovered(true)} onMouseOut={() => setIsMyCompetenciesHovered(false)} className={navButtonClasses.join(' ')} title="Show My Competencies" tabIndex={(isApplicationMenuVisible ? -1 : 0)}>
              {isMobileClient && (
                <>
                  <span className='sr-only'>My Competencies</span>
                  <Icon name='icon-btn-competencies-mobile' className={mobileNavIconClasses.join(' ')} />
                </>
              )}
              {!isMobileClient && (
                <>
                  <Icon name='icon-bulb' className={getNavIconClasses(isMyCompetenciesHovered)} />
                  <span className={getNavLabelClasses(isMyCompetenciesHovered)}>My Competencies</span>
                </>
              )}
            </Link>
          </div>
          <button onMouseOver={() => setIsHamburgerHovered(true)} onMouseOut={() => setIsHamburgerHovered(false)} className='btn-icon' id='hamburger' tabIndex={(isApplicationMenuVisible ? -1 : 0)} onClick={applicationMenuClick} title='Show application menu' aria-haspopup='true' aria-expanded={(isApplicationMenuVisible ? true : false)} aria-controls='mainMenu'>
            <span className='sr-only'>Application Menu</span>
            <Icon name='icon-hamburger' className={hamburgerClasses.join(' ')} />
          </button>
        </section>
      </section>
      <section className={applicationMenuContainerClasses.join(' ')}>
        <button className='close-button' title='Close application menu' onClick={applicationMenuClick}>&times;</button>
        <ul className='application-menu' id='mainMenu' role='menu' aria-labelledby='hamburger'>
          <li>
            <Link to={ROUTES.home}>Home</Link>
          </li>
          <li>
            <Link to={ROUTES.faq}>FAQ</Link>
          </li>
          <li>
            <Link to={ROUTES.skillPath.core}>Core Business Skills</Link>
          </li>
          <li>
            <Link to={ROUTES.skillPath.discipline}>Discipline Specific Skills</Link>
          </li>
          <li>
            <Link to={ROUTES.skillPath.projectManager}>Project Manager Skills</Link>
          </li>
          <li>
            <Link to={ROUTES.skillPath.subjectMatterExpert}>Subject Matter Expert Skills</Link>
          </li>
          <li>
            <Link to={ROUTES.skillPath.leaderManager}>Leader/Manager Skills</Link>
          </li>
        </ul>
        <hr/>
        <ul className='application-menu' id='bookmarkMenu' aria-labelledby='hamburger'>
                  <li><Link to={ROUTES.bookmarks.root}>My Bookmarks</Link></li>
                {roles.indexOf('Progression.Manage') > -1 && (  <li><Link to={ROUTES.supervisor.employeeList}>My Team</Link></li>)}
                {roles.indexOf('CareerFramework.Admin') > -1 && <li className='m-b-0-important'><Link to={ROUTES.reports}>Reports</Link></li>}
                  </ul>
        {roles.indexOf('CareerFramework.Admin') > -1 && (
        <><hr/>
        <ul  className='application-menu' id='adminMenu' role='CareerFramework.Admin' aria-labelledby='hamburger'>

            <><li><Link to={ROUTES.admin.root}>Edit Framework</Link></li>
            <li><Link to={ROUTES.admin.editFaqs}>Edit FAQs</Link></li>
            <li><Link to={ROUTES.admin.editCompetencyPeriod}>Edit Competency Period</Link></li>
            <li className='m-b-0-important'><Link to={ROUTES.admin.editQuiz}>Edit Quiz</Link></li>
            
            </>
        </ul></>
                  )}
      </section>
    </nav>
  )
};

export default Nav;